import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type { CreativeTemplateDto, CreativeTemplateMacrosDto } from '@/services/ws-appnexus-proxy/src/main/resources/rest-api';
import { SupportedMacroTypes } from '@/services/ws-appnexus-proxy/src/main/resources/rest-api';
import {
  AcTemplateMacroType,
  CreativeType,
  LanguageType,
  ThirdPartyPixelFormat,
} from '@/services/ws-creatives/src/main/resources/rest-api';
import type {
  AcTemplateMacroDto,
  AcTemplateDto,
  CampaignPositionDto,
  HtmlAssetDto,
  IdAccountDto,
  ImageDto,
  MediaAssetDto,
} from '@/services/ws-creatives/src/main/resources/rest-api';
import { optionSchema } from '@/js/libs/form';
import { isUrlValid, ProtocolMode } from '@/js/libs/utils/is-url-valid';

import { MAX_IMAGE_SIZE_IN_BYTES } from './constants.ts';
import { isContentPixelFormat } from './helpers.ts';

const INTEGER_REGEX = /^\d+$/;
const DECIMAL_REGEX = /^\d+(\.\d{1,2})?$/;

export enum CreativeFormSearchKey {
  ACCOUNT_ID = 'accountId',
}

/** Account a new creative starts with, carried over from the account filter of the creatives list. */
export const creativeFormRouterSearchParamsSchema = z.object({
  [CreativeFormSearchKey.ACCOUNT_ID]: z.custom<IdAccountDto>().optional(),
});

export type CreativeFormSearchParams = z.infer<typeof creativeFormRouterSearchParamsSchema>;

export const useCreativeFormSchema = () => {
  const [t] = useTranslation(['common', 'creatives', 'messages']);

  // The ad servers reject oversized banners, so the limit is enforced on submit and not only hinted in the preview.
  const validateImageSize = (images: ImageDto[], ctx: z.RefinementCtx) => {
    images.forEach((image, index) => {
      if ((image.sizeInBytes ?? 0) > MAX_IMAGE_SIZE_IN_BYTES) {
        ctx.addIssue({ code: 'custom', path: ['images', index], message: t('creatives:IMAGE_SIZE_TOO_BIG') });
      }
    });
  };

  const validateLink = (link: string | null | undefined, ctx: z.RefinementCtx) => {
    if (!link) {
      ctx.addIssue({ code: 'custom', path: ['link'], message: t('common:REQUIRED_FIELD') });
    } else if (!isUrlValid(link, { protocolMode: ProtocolMode.REQUIRED, strict: false })) {
      ctx.addIssue({ code: 'custom', path: ['link'], message: t('common:INVALID_URL') });
    }
  };

  const validateViewpixels = (viewpixels: { value: string }[], ctx: z.RefinementCtx) => {
    viewpixels.forEach(({ value }, index) => {
      if (!value) {
        ctx.addIssue({ code: 'custom', path: ['viewpixels', index, 'value'], message: t('common:REQUIRED_FIELD') });
        return;
      }
      if (!isUrlValid(value, { protocolMode: ProtocolMode.REQUIRED, strict: false })) {
        ctx.addIssue({ code: 'custom', path: ['viewpixels', index, 'value'], message: t('common:INVALID_URL') });
      }
    });
  };

  const validateThirdPartyPixels = (pixels: { format: { value: ThirdPartyPixelFormat }; value: string }[], ctx: z.RefinementCtx) => {
    pixels.forEach(({ format, value }, index) => {
      const trimmedValue = value.trim();
      if (!trimmedValue) {
        ctx.addIssue({ code: 'custom', path: ['thirdPartyPixels', index, 'value'], message: t('common:REQUIRED_FIELD') });
        return;
      }
      if (!isContentPixelFormat(format.value) && !isUrlValid(trimmedValue, { protocolMode: ProtocolMode.REQUIRED, strict: false })) {
        ctx.addIssue({ code: 'custom', path: ['thirdPartyPixels', index, 'value'], message: t('common:INVALID_URL') });
      }
    });
  };

  const validateBannerSize = (width: number | null, height: number | null, ctx: z.RefinementCtx) => {
    if (width === null) {
      ctx.addIssue({ code: 'custom', path: ['width'], message: t('common:REQUIRED_FIELD') });
    } else if (width < 1) {
      ctx.addIssue({ code: 'custom', path: ['width'], message: t('messages:LIMIT_EXCEEDED_MIN', { min: 1 }) });
    }
    if (height === null) {
      ctx.addIssue({ code: 'custom', path: ['height'], message: t('common:REQUIRED_FIELD') });
    } else if (height < 1) {
      ctx.addIssue({ code: 'custom', path: ['height'], message: t('messages:LIMIT_EXCEEDED_MIN', { min: 1 }) });
    }
  };

  const validateAcMacros = (macros: AcTemplateMacroDto[], macroses: Record<string, string> | null, ctx: z.RefinementCtx) => {
    macros.forEach((macro) => {
      const value = macroses?.[macro.code] ?? '';
      if (macro.isRequired && !value) {
        ctx.addIssue({ code: 'custom', path: ['macroses', macro.code], message: t('common:REQUIRED_FIELD') });
        return;
      }
      if (!value) {
        return;
      }
      if (macro.type === AcTemplateMacroType.URL && !isUrlValid(value, { protocolMode: ProtocolMode.REQUIRED, strict: false })) {
        ctx.addIssue({ code: 'custom', path: ['macroses', macro.code], message: t('common:INVALID_URL') });
      }
      if (macro.type === AcTemplateMacroType.INTEGER && !INTEGER_REGEX.test(value)) {
        ctx.addIssue({ code: 'custom', path: ['macroses', macro.code], message: t('common:INVALID_INTEGER') });
      }
      if (macro.type === AcTemplateMacroType.DECIMAL && !DECIMAL_REGEX.test(value)) {
        ctx.addIssue({ code: 'custom', path: ['macroses', macro.code], message: t('common:INVALID_DECIMAL') });
      }
    });
  };

  const validateXandrMacros = (macros: CreativeTemplateMacrosDto[], macroses: Record<string, string> | null, ctx: z.RefinementCtx) => {
    macros.forEach((macro) => {
      const value = macroses?.[macro.code] ?? '';
      if (macro.isRequired && !value) {
        ctx.addIssue({ code: 'custom', path: ['macroses', macro.code], message: t('common:REQUIRED_FIELD') });
        return;
      }
      if (!value) {
        return;
      }
      if (macro.type === SupportedMacroTypes.url && !isUrlValid(value, { protocolMode: ProtocolMode.REQUIRED, strict: false })) {
        ctx.addIssue({ code: 'custom', path: ['macroses', macro.code], message: t('common:INVALID_URL') });
      }
      if (macro.type === SupportedMacroTypes.integer && !INTEGER_REGEX.test(value)) {
        ctx.addIssue({ code: 'custom', path: ['macroses', macro.code], message: t('common:INVALID_INTEGER') });
      }
      if (macro.type === SupportedMacroTypes.decimal && !DECIMAL_REGEX.test(value)) {
        ctx.addIssue({ code: 'custom', path: ['macroses', macro.code], message: t('common:INVALID_DECIMAL') });
      }
    });
  };

  return z
    .object({
      // base
      account: optionSchema(z.custom<IdAccountDto>()).nullable(),
      creativeType: optionSchema(z.nativeEnum(CreativeType)),
      name: z.string().min(1, t('common:REQUIRED_FIELD')),

      // native / native_video
      languageType: z.nativeEnum(LanguageType),
      viewpixels: z.array(z.object({ value: z.string() })),
      title: z.string(),
      description: z.string(),
      link: z.string(),
      linkText: z.string(),
      alternativeTitle: z.string(),

      // banner
      width: z.number().nullable(),
      height: z.number().nullable(),
      shouldSaveSize: z.boolean(),
      content: z.string(),

      // ac
      acTemplate: optionSchema(z.custom<AcTemplateDto>()).nullable(),
      adServerType: optionSchema(z.string()).nullable(),
      macroses: z.record(z.string(), z.string()).nullable(),
      macroFileNames: z.record(z.string(), z.string()),
      positions: z.array(optionSchema(z.custom<CampaignPositionDto>())),
      thirdPartyPixels: z.array(z.object({ format: optionSchema(z.nativeEnum(ThirdPartyPixelFormat)), value: z.string() })),
      images: z.array(z.custom<ImageDto>()),

      // html_tag
      creativeTemplate: optionSchema(z.custom<CreativeTemplateDto>()).nullable(),

      // html
      htmlAsset: z.custom<HtmlAssetDto>().nullable(),

      // video
      mediaAsset: z.custom<MediaAssetDto>().nullable(),
    })
    .superRefine((values, ctx) => {
      // Common fields validations
      if (!values.account) {
        ctx.addIssue({ code: 'custom', path: ['account'], message: t('common:REQUIRED_FIELD') });
      }

      const type = values.creativeType?.value;

      if (type === CreativeType.NATIVE) {
        if (!values.images[0]) {
          ctx.addIssue({ code: 'custom', path: ['images', 0], message: t('common:REQUIRED_FIELD') });
        }
        if (!values.images[1]) {
          ctx.addIssue({ code: 'custom', path: ['images', 1], message: t('common:REQUIRED_FIELD') });
        }
        if (!values.title) {
          ctx.addIssue({ code: 'custom', path: ['title'], message: t('common:REQUIRED_FIELD') });
        }
        if (!values.description) {
          ctx.addIssue({ code: 'custom', path: ['description'], message: t('common:REQUIRED_FIELD') });
        }
        if (!values.alternativeTitle) {
          ctx.addIssue({ code: 'custom', path: ['alternativeTitle'], message: t('common:REQUIRED_FIELD') });
        }
        if (!values.linkText) {
          ctx.addIssue({ code: 'custom', path: ['linkText'], message: t('common:REQUIRED_FIELD') });
        }
        validateLink(values.link, ctx);
        validateViewpixels(values.viewpixels, ctx);
      }

      if (type === CreativeType.NATIVE_VIDEO) {
        if (!values.mediaAsset) {
          ctx.addIssue({ code: 'custom', path: ['mediaAsset'], message: t('common:REQUIRED_FIELD') });
        }
        if (!values.title) {
          ctx.addIssue({ code: 'custom', path: ['title'], message: t('common:REQUIRED_FIELD') });
        }
        if (!values.description) {
          ctx.addIssue({ code: 'custom', path: ['description'], message: t('common:REQUIRED_FIELD') });
        }
        if (!values.alternativeTitle) {
          ctx.addIssue({ code: 'custom', path: ['alternativeTitle'], message: t('common:REQUIRED_FIELD') });
        }
        if (!values.linkText) {
          ctx.addIssue({ code: 'custom', path: ['linkText'], message: t('common:REQUIRED_FIELD') });
        }
        validateLink(values.link, ctx);
        validateViewpixels(values.viewpixels, ctx);
      }

      if (type === CreativeType.VIDEO) {
        if (!values.mediaAsset) {
          ctx.addIssue({ code: 'custom', path: ['mediaAsset'], message: t('common:REQUIRED_FIELD') });
        }
        validateLink(values.link, ctx);
      }

      // AC validations
      if (type === CreativeType.AC) {
        if (!values.acTemplate) {
          ctx.addIssue({ code: 'custom', path: ['acTemplate'], message: t('common:REQUIRED_FIELD') });
        }
        const macros = values.acTemplate?.value?.macros ?? [];
        validateAcMacros(macros, values.macroses, ctx);
        validateBannerSize(values.width, values.height, ctx);
        validateThirdPartyPixels(values.thirdPartyPixels, ctx);
      }

      // Banner (Image) validations
      if (type === CreativeType.BANNER) {
        if (!values.creativeTemplate) {
          ctx.addIssue({ code: 'custom', path: ['creativeTemplate'], message: t('common:REQUIRED_FIELD') });
        }
        if (!values.images[0]) {
          ctx.addIssue({ code: 'custom', path: ['images', 0], message: t('common:REQUIRED_FIELD') });
        }
        const macros = values.creativeTemplate?.value?.macroses ?? [];
        validateXandrMacros(macros, values.macroses, ctx);
        validateBannerSize(values.width, values.height, ctx);
        validateLink(values.link, ctx);
        validateThirdPartyPixels(values.thirdPartyPixels, ctx);
        validateImageSize(values.images, ctx);
      }

      // Banner (Script) validations
      if (type === CreativeType.HTML_TAG) {
        if (!values.creativeTemplate) {
          ctx.addIssue({ code: 'custom', path: ['creativeTemplate'], message: t('common:REQUIRED_FIELD') });
        }
        const macros = values.creativeTemplate?.value?.macroses ?? [];
        validateXandrMacros(macros, values.macroses, ctx);
        validateBannerSize(values.width, values.height, ctx);
        validateThirdPartyPixels(values.thirdPartyPixels, ctx);
      }

      // Newsletter display validations
      if (type === CreativeType.NEWSLETTER_DISPLAY) {
        if (!values.images[0]) {
          ctx.addIssue({ code: 'custom', path: ['images', 0], message: t('common:REQUIRED_FIELD') });
        }
        validateLink(values.link, ctx);
        validateImageSize(values.images, ctx);
      }

      // Newsletter native validations
      if (type === CreativeType.NEWSLETTER_NATIVE) {
        if (!values.images[0]) {
          ctx.addIssue({ code: 'custom', path: ['images', 0], message: t('common:REQUIRED_FIELD') });
        }
        if (!values.title) {
          ctx.addIssue({ code: 'custom', path: ['title'], message: t('common:REQUIRED_FIELD') });
        }
        if (!values.description) {
          ctx.addIssue({ code: 'custom', path: ['description'], message: t('common:REQUIRED_FIELD') });
        }
        validateLink(values.link, ctx);
      }

      // Banner (Zip) validations
      if (type === CreativeType.HTML_ZIP) {
        if (!values.htmlAsset) {
          ctx.addIssue({ code: 'custom', path: ['htmlAsset'], message: t('common:REQUIRED_FIELD') });
        }
        validateLink(values.link, ctx);
        validateBannerSize(values.width, values.height, ctx);
        validateThirdPartyPixels(values.thirdPartyPixels, ctx);
      }
    });
};

type Schema = ReturnType<typeof useCreativeFormSchema>;
export type FormValues = z.input<Schema>;
export type FormSubmitValues = z.output<Schema>;
